import { useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "store/reducers/Reducer";
import { setActiveWorkspace, setLastAccessedWorkspace } from "store/reducers/workspace";
import { useWorkspaceApi } from "./useWorkspaceApi";
import { useUserCatalog } from "hooks/useSession";
import { useOptionalUserPreferences } from "providers/user-preferences";

/**
 * Custom hook to get workspace identifiers from URL params with project-aware fallback
 * Provides workspaceId and validation state
 *
 * Priority order:
 * 1. Workspace that owns the current project (if projectId in URL)
 * 2. URL workspace ID (match in loaded list, otherwise the raw URL id — list may lag or omit entries)
 * 3. Last accessed workspace from Redux
 * 4. Recommended workspace
 *
 * @returns Object containing workspaceId, loading state, and validation
 */
export const useWorkspaceIdentifiers = () => {
  const userPreferencesContext = useOptionalUserPreferences();
  const userPreferences = userPreferencesContext?.preferences ?? {};
  const patchPreferences = userPreferencesContext?.patchPreferences;
  const arePreferencesHydrated = userPreferencesContext?.isHydrated ?? false;
  const userId = userPreferencesContext?.userId ?? null;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId: urlWorkspaceId, projectId: urlProjectId } = useParams<{
    workspaceId: string;
    projectId: string;
  }>();
  const reduxLastAccessedWorkspaceId = useSelector(
    (state: AppState) => state.workspace?.lastAccessedWorkspaceId
  );
  const lastAccessedWorkspaceId =
    reduxLastAccessedWorkspaceId ?? userPreferences.navigation?.lastWorkspaceId ?? null;

  // Get workspaces and recommended workspace from workspace API
  const { workspaces, recommendedWorkspace } = useWorkspaceApi({ take: 50 });
  const { data: catalog } = useUserCatalog();

  useEffect(() => {
    const savedWorkspaceId = userPreferences.navigation?.lastWorkspaceId;
    if (!reduxLastAccessedWorkspaceId && savedWorkspaceId) {
      dispatch(setLastAccessedWorkspace(savedWorkspaceId));
    }
  }, [dispatch, reduxLastAccessedWorkspaceId, userPreferences.navigation?.lastWorkspaceId]);

  // Helper to find which workspace owns a project
  const findProjectWorkspaceId = useMemo(() => {
    if (!urlProjectId || urlProjectId === "0" || !catalog?.workspaces) return null;

    for (const workspace of catalog.workspaces) {
      const project = workspace.projects?.find((p) => p.project_id.toString() === urlProjectId);
      if (project) {
        return workspace.workspace_id;
      }
    }
    return null;
  }, [urlProjectId, catalog?.workspaces]);

  // Map workspace ID from URL to actual workspace ID
  const workspaceId = useMemo(() => {
    // PRIORITY 1: If there's a project in the URL, find which workspace owns it
    if (urlProjectId && urlProjectId !== "0" && catalog?.workspaces) {
      const projectWorkspaceId = findProjectWorkspaceId;
      if (projectWorkspaceId) {
        return projectWorkspaceId;
      }
    }

    // PRIORITY 2: If URL has workspace ID, use it (workspace list is paginated / can lag on first paint)
    if (urlWorkspaceId && urlWorkspaceId !== "0") {
      const workspaceById = workspaces.find((w) => w.id === urlWorkspaceId);
      if (workspaceById) {
        return workspaceById.id;
      }
      return urlWorkspaceId;
    }

    // PRIORITY 3: Fallback to last accessed workspace
    if (lastAccessedWorkspaceId) {
      return lastAccessedWorkspaceId;
    }

    // PRIORITY 4: Fallback to recommended workspace
    if (recommendedWorkspace) {
      return recommendedWorkspace.id;
    }

    return null;
  }, [
    urlWorkspaceId,
    urlProjectId,
    workspaces,
    catalog?.workspaces,
    lastAccessedWorkspaceId,
    recommendedWorkspace,
    findProjectWorkspaceId,
  ]);

  // Only a workspace the user opened (its URL, or the owner of the project in
  // the URL) counts as "last opened". Fallback picks on pages without a
  // workspace must not overwrite it, or "/" drifts to the wrong workspace.
  const isOpenedFromUrl =
    Boolean(workspaceId) &&
    (workspaceId === findProjectWorkspaceId || workspaceId === urlWorkspaceId) &&
    workspaces.some((workspace) => workspace.id === workspaceId);

  // Persist workspace to Redux whenever it changes
  useEffect(() => {
    if (workspaceId) {
      dispatch(setActiveWorkspace(workspaceId));
      dispatch(setLastAccessedWorkspace(workspaceId));
      if (
        isOpenedFromUrl &&
        userId &&
        arePreferencesHydrated &&
        userPreferences.navigation?.lastWorkspaceId !== workspaceId
      ) {
        patchPreferences?.({ navigation: { lastWorkspaceId: workspaceId } });
      }
    }
  }, [
    arePreferencesHydrated,
    dispatch,
    isOpenedFromUrl,
    patchPreferences,
    userId,
    userPreferences.navigation?.lastWorkspaceId,
    workspaceId,
  ]);

  // Correct URL if workspace doesn't match project's actual workspace
  useEffect(() => {
    if (
      urlProjectId &&
      urlProjectId !== "0" &&
      urlWorkspaceId &&
      findProjectWorkspaceId &&
      location.pathname.includes("/workspaces/")
    ) {
      // If URL workspace doesn't match project's actual workspace, redirect
      if (findProjectWorkspaceId !== urlWorkspaceId) {
        const pathSegments = location.pathname.split("/");
        // Update workspace in URL (should be at index 2: /workspaces/{workspaceId}/...)
        if (pathSegments[1] === "workspaces" && pathSegments[2]) {
          pathSegments[2] = findProjectWorkspaceId;
          const correctedPath = pathSegments.join("/");
          navigate(correctedPath, { replace: true });
        }
      }
    }
  }, [urlProjectId, urlWorkspaceId, findProjectWorkspaceId, location.pathname, navigate]);

  const hasValidWorkspaceId = Boolean(workspaceId);

  return {
    workspaceId,
    hasValidWorkspaceId,
    isLoading: false, // No async loading for URL params
  };
};
